import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { ArrowRight } from 'lucide-react';
import { getCategoryImage } from '@/lib/categoryImages';

const tileTag = (slug) => ({
  lehenga: 'Bridal Couture', blouse: 'Fitted', kurti: 'Everyday', suit: 'Occasion',
  'ready-to-wear-saree': 'Pre-stitched', 'co-ord-set': 'Contemporary', dress: 'Resort',
  top: 'Everyday', skirt: 'Draped', pant: 'Tailored', others: 'Bespoke',
}[slug] || 'Signature');

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/categories').then((res) => setCategories(res.data.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-background pt-24 md:pt-32" data-testid="categories-page">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-24 pb-24">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16 md:mb-20">
          <div>
            <div className="eyebrow mb-6" style={{ fontSize: 15, fontWeight: 700 }}>The Collection</div>
            <h1 className="font-display font-light text-ink leading-[0.98] tracking-[-0.02em]" style={{ fontSize: 'clamp(48px, 7vw, 96px)' }}>
              Tailoring,<br />every silhouette.
            </h1>
          </div>
          <p className="text-ink/75 max-w-md" style={{ fontSize: 17, lineHeight: 1.75 }}>
            Eleven expertly-crafted categories. Each garment measured, cut, and stitched to be uniquely yours. Explore the range and begin your commission.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[3/4] bg-secondary animate-pulse" />
            ))}
          </div>
        ) : (
          /* SYMMETRIC GRID — every tile identical aspect so full images render */
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
            {categories.map((cat) => {
              const src = getCategoryImage(cat);
              return (
                <Link
                  key={cat.id}
                  to={`/categories/${cat.slug}`}
                  data-testid={`cat-link-${cat.slug}`}
                  className="group"
                >
                  <div className="tile-zoom relative aspect-[3/4] bg-secondary overflow-hidden">
                    {src && <img src={src} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />}
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display text-ink leading-tight" style={{ fontSize: 28 }}>{cat.name}</div>
                      {cat.starting_price ? (
                        <div className="mt-2 uppercase tracking-luxe text-[11px] text-ink-muted">Starting at <span className="text-ink font-semibold tabular-nums">₹{cat.starting_price}</span></div>
                      ) : null}
                    </div>
                    <ArrowRight className="w-4 h-4 text-ink-muted flex-shrink-0 mt-2 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
